import { ROUND_SECONDS, type Side } from "./stuff.ts";

/**
 * Milestone 4, Live. Each player's game posts its state (CS2 Game State
 * Integration) to the API; the Live page follows it.
 */

/** The match as CS2 names its phases. */
export type MatchPhase = "warmup" | "live" | "intermission" | "gameover";
/** The round as CS2 names its phases. */
export type RoundPhase = "freezetime" | "live" | "over";

export interface LiveScore {
  ct: number;
  t: number;
}

/** A match the player left (or that ended): what the game-over screen shows. */
export interface LiveLastGame {
  map: string;
  score: LiveScore | null;
  side: Side | null;
  /** The game's gameStartedAt: the game-over screen is shown once per game, ended or left. */
  startedAt: string | null;
  endedAt: string;
}

/** One player's game, as their CS2 last described it. */
export interface LiveState {
  userId: string;
  /** de_mirage; null when the player is in the menu or the game is closed. */
  map: string | null;
  mode: string | null;
  matchPhase: MatchPhase | null;
  roundPhase: RoundPhase | null;
  round: number | null;
  score: LiveScore | null;
  /** Only ever read from payloads about the player themselves, never from a teammate they watch. */
  side: Side | null;
  /** When the round phase last changed: the round clock is estimated from it. */
  roundPhaseSince: string | null;
  /** When the bomb went down this round; null otherwise. */
  bombPlantedAt: string | null;
  /** When this match started being followed. */
  gameStartedAt: string | null;
  lastGame: LiveLastGame | null;
  /** The last payload from the game, heartbeats included (about every 10 s when nothing changes). */
  updatedAt: string;
}

export interface LiveMember {
  id: string;
  name: string;
  avatarUrl: string | null;
}

/** What the Live page loads first: who is in the team, and every member's game. */
export interface LiveSnapshot {
  me: string;
  members: LiveMember[];
  states: LiveState[];
  /** The server's clock, so the page can tell how old an update is whatever its own clock says. */
  now: string;
}

/** The player's Live config: where the game sends, and whether it has. */
export interface LiveSetup {
  fileName: string;
  url: string;
  lastSeenAt: string | null;
  /** Set when a config of this player sends data for another Steam account. */
  otherAccount: boolean;
}

/** One teammate's learning status on a stuff, for the Live radar. */
export interface LiveTracked {
  stuffId: string;
  userId: string;
  status: "to_learn" | "practising" | "learned";
}

/** Heartbeats come every 10 s in a match: no update for longer than this means the feed is lost. */
export const LIVE_STALE_MS = 30_000;
/** A game not heard from for this long is over, whatever its last state said. */
export const LIVE_FORGET_MS = 30 * 60_000;

/**
 * The round clock (seconds left, counting down from 1:55), estimated from when the
 * round went live: the game does not send the countdown to players. Null outside a live round.
 */
export function estimateClock(state: Pick<LiveState, "roundPhase" | "roundPhaseSince">, now: number): number | null {
  if (state.roundPhase === "freezetime") return ROUND_SECONDS;
  if (state.roundPhase !== "live" || !state.roundPhaseSince) return null;
  const elapsed = (now - Date.parse(state.roundPhaseSince)) / 1000;
  return Math.max(0, Math.round(ROUND_SECONDS - elapsed));
}

/** Seconds left on the bomb (40 s from the plant), or null without a planted bomb. */
export function bombSecondsLeft(state: Pick<LiveState, "bombPlantedAt">, now: number): number | null {
  if (!state.bombPlantedAt) return null;
  return Math.max(0, 40 - Math.floor((now - Date.parse(state.bombPlantedAt)) / 1000));
}

/** True when two players' games are the same match: same map and mode, same round and score give or take one round. */
export function sameMatch(a: LiveState, b: LiveState): boolean {
  if (!a.map || !b.map || a.map !== b.map || a.mode !== b.mode) return false;
  if (!a.score || !b.score) return a.matchPhase === "warmup" && b.matchPhase === "warmup";
  const ra = a.score.ct + a.score.t;
  const rb = b.score.ct + b.score.t;
  if (Math.abs(ra - rb) > 1) return false;
  // Same round: the scores must be the same. One round apart: they differ by that round only.
  if (ra === rb) return a.score.ct === b.score.ct && a.score.t === b.score.t;
  const [lo, hi] = ra < rb ? [a.score, b.score] : [b.score, a.score];
  return hi.ct >= lo.ct && hi.t >= lo.t;
}
